import { commonFilterOptions } from 'utils/dateRangeFilterOptions';

// These correspond to variable names in the database. The client uses its own strings;
// For example, the SUMMARY column in the database is called "Subject" in the client. See en.json.
export const ASSET_SUBTYPES = {
  MICROSOFT_TEAMS: 'MICROSOFT_TEAMS',
  MICROSOFT_TEAMS_MEETING: 'MICROSOFT_TEAMS_MEETING',
  SLACK: 'SLACK',
  XM_BRIDGE: 'XM_BRIDGE',
  ZOOM: 'ZOOM',
  CUSTOM: 'CUSTOM',
};

export const ASSET_TYPES = {
  COMMUNICATION: 'COMMUNICATION',
};

export const COLUMN_NAMES = {
  CHECKBOX: 'CHECKBOX',
  SEVERITY: 'SEVERITY',
  SUMMARY: 'SUMMARY',
  TYPE: 'TYPE',
  CREATED_AT: 'CREATED_AT',
  STATUS: 'STATUS',
  OWNER: 'OWNER',
  INCIDENT_ID: 'INCIDENT_ID',
  PIR: 'PIR',
  IMPACTED_SERVICE: 'IMPACTED_SERVICE',
  TIME_TO_RESOLVE: 'TIME_TO_RESOLVE',
  TIME_TO_ACKNOWLEDGE: 'TIME_TO_ACKNOWLEDGE',
};

export const INCIDENT_IDENTIFIER = 'INCIDENT_IDENTIFIER';

export const INCIDENT_OWNERS = {
  MY_INCIDENTS: 'MY_INCIDENTS',
  ALL_INCIDENTS: 'ALL_INCIDENTS',
};

export const INCIDENT_STATUS = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  MITIGATED: 'MITIGATED',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
};

export const IncidentConsoleWidgetTypes = {
  AUTOMATION: 'AUTOMATION',
  COLLABORATION: 'COLLABORATION',
  IMPACTED_SERVICES: 'IMPACTED_SERVICES',
  LINKED_INCIDENTS: 'LINKED_INCIDENTS',
  STAKEHOLDERS: 'STAKEHOLDERS',
  TASKS: 'TASKS',
  TIMELINE: 'TIMELINE',
  AFFECTED_SITES: 'AFFECTED_SITES',
  ATTACHMENTS: 'ATTACHMENTS',
};

export const isWidgetTypeEnabledByDefault = type =>
  type !== IncidentConsoleWidgetTypes.AFFECTED_SITES;

export const defaultIncidentConsoleWidgets = Object.values(
  IncidentConsoleWidgetTypes,
).filter(isWidgetTypeEnabledByDefault);

export const isClosedStatus = value =>
  [INCIDENT_STATUS.RESOLVED, INCIDENT_STATUS.REJECTED].includes(
    value.toUpperCase(),
  );

// Meant to be used by Incident Management actions. If we have more URLs, move them to a separate file.
export const ROOT_URL = '/webapi/xm/1/incidents';

export const SEVERITY_LEVELS = {
  MINIMAL: 'MINIMAL',
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

export const DEFAULT_SEVERITY = { name: 'Minimal', level: 100 };

export const MAX_SELECTABLE_INCIDENTS = 100;

export const INCIDENT_TIMELINE_BATCH_SIZE = 100;
export const INCIDENT_POLL_PERIOD = 5000;
export const INCIDENT_DURATION_UPDATE = 1000;
export const INCIDENT_TIMELINE_MAX_SEARCH_LENGTH = 128;

export const INCIDENT_RESOLVER_STATE = {
  ACTIVE: 'ACTIVE',
  STOPPED: 'STOPPED',
  DISMISSED: 'DISMISSED',
  NO_RESPONSE: 'NO_RESPONSE',
  ESCALATED: 'ESCALATED',
  ENGAGED: 'ENGAGED',
  FAILED: 'FAILED',
};

export const INCIDENT_RESOLVER_STATE_TO_MSG_ID = {
  ACTIVE: 'NOTIFYING_DOT',
  STOPPED: 'STOPPED',
  DISMISSED: 'DISMISSED',
  NO_RESPONSE: 'NO_RESPONSE',
  ESCALATED: 'ESCALATED',
  ENGAGED: 'ENGAGED',
  FAILED: 'FAILED',
};

// enum for whether to show a confirmation
// modal with a field for "rationale"
export const incFieldsWithRationale = {
  none: 'none',
  owner: 'owner',
  summary: 'summary',
  severity: 'severity',
  status: 'status',
};

export const defaultDropdownFilters = {
  dateFilterType: 'ALL_DATES',
  statusFilter: [
    INCIDENT_STATUS.OPEN,
    INCIDENT_STATUS.IN_PROGRESS,
    INCIDENT_STATUS.MITIGATED,
    INCIDENT_STATUS.RESOLVED,
  ],
  severityFilter: [],
  ownerFilter: [],
  incidentCommanderFilter: [],
  impactedServiceFilter: [],
  engagedResolverFilter: [],
  targetedResolverFilter: [],
  workflow: [],
  typeFilter: [],
};

// Any time a new entry is added here, also modify the entries in ViewIncidents/utils.js, so they appear in the URL.
export const initialFilterState = {
  dateRange: {
    dateFilterType: commonFilterOptions.ALL_DATES.value,
    isDirty: false, // TODO: Figure out what this does, and can we remove it?
  },
  impactedServiceFilter: [],
  matchingIncidentsCount: 0,
  ownerFilter: [],
  incidentCommanderFilter: [],
  search: '',
  severityFilter: [],
  statusFilter: defaultDropdownFilters.statusFilter,
  engagedResolverFilter: [],
  engagedResolver: [],
  targetedResolverFilter: [],
  targetedResolver: [],
  propertyValues: [],
  propertyNames: [],
  workflow: [],
  workflows: [],
  typeFilter: [],
};

export const DATE_FILTER_OPTIONS = [
  commonFilterOptions.ALL_DATES,
  commonFilterOptions.PAST_24HOURS,
  commonFilterOptions.PAST_7DAYS,
  commonFilterOptions.PAST_30DAYS,
  commonFilterOptions.PAST_60DAYS,
  commonFilterOptions.PAST_90DAYS,
  commonFilterOptions.DATE_RANGE,
];

export const TIMELINE_ENTRY_TYPES = [
  '',
  'AUTOMATION',
  'COLLABORATION',
  'NOTE',
  'RESOLVER',
  'STAKEHOLDER',
  'UPDATE',
  'INCIDENT_LINK',
  'TASK',
  'ATTACHMENT',
];

export const ASSET_NAME_LENGTH = {
  [ASSET_SUBTYPES.XM_BRIDGE]: 100,
  [ASSET_SUBTYPES.SLACK]: 80,
  [ASSET_SUBTYPES.MS_TEAM]: 50,
  [ASSET_SUBTYPES.MICROSOFT_TEAMS]: 50,
  [ASSET_SUBTYPES.ZOOM]: 128,
};

export const MAX_STAKEHOLDERS = 25;

export const INCIDENT_STAKEHOLDERS_NOTIFIED = 'INCIDENT_STAKEHOLDERS_NOTIFIED';

export const INITIATE_INCIDENT = 'Initiate Incident';

// We use this id to calculate boundaries on table elements
export const INCIDENT_TABLE_ID = 'view-incidents-table';

export const XM_INCIDENT_DETAILS_ID = '#xm-incident-details';

export const INCIDENTS_LIST_FILTERS = 'xmIncidentsListFilters';

export const SEVERITY_COLORS = [
  // From Critical to Minimal
  '#C62437',
  '#E9724C',
  '#ECA600',
  '#1657CA',
  '#4697E7',
];

export const METRICS_COLORS = ['#A5549C', '#419AB1'];

export const baseIncidentsUri = '/api/xm/1/incidents';

export const DELETED_USER_TYPES = {
  INCIDENT_COMMANDER: 'INCIDENT_COMMANDER',
  RESOLVER: 'RESOLVER',
  STAKEHOLDER: 'STAKEHOLDER',
  ENGAGED_RESOLVER: 'ENGAGED_RESOLVER',
};

// https://trail.everbridge.com/browse/COR-68677
// A list of Tools step that are technically not supposed to be returned by xm-api
// if INCIDENT_MANAGEMENT_MVP is turned off. But that server work is too messy,
// so we're dealing with it here. Hopefully it's temporary.
// They're fetched by GET script-steps?scriptStepType=BUILT_IN
export const IncidentManagementMVPSteps = [
  'Add Collaboration Channel to Incident',
  'Add Note to Incident',
  'Get Incident',
  'Get Incident Collaboration Channels',
  'Map Incident Severity',
  'Update Incident',
  'Add Change Record',
  'Initiate Incident', // xm-api won't actually return this if FF is off, but included for completion purpose.
];

export const PROPERTY_SIDE_PANEL_MODE = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
};

export const INCIDENT_LIST_POLLING_PERIOD = 20000;
